import { useCallback, useEffect, useState } from "react";
import { Check, ExternalLink, Loader2, RefreshCw, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";

const TABS = [
  { key: "player", label: "Players" },
  { key: "news", label: "News" },
];
const JOBS = [
  { key: "player", label: "Player research" },
  { key: "news", label: "News research" },
  { key: "counties", label: "County data" },
  { key: "finance", label: "Finance data" },
];
const STATUSES = ["pending", "approved", "rejected", "all"];

export default function Discoveries() {
  const [kind, setKind] = useState("player");
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const { data } = await api.get(`/admin/discoveries?kind=${kind}&status=${status}`);
      setItems(data);
    } catch (err) {
      setFailed(true);
      setItems([]);
      toast.error(errText(err));
    } finally {
      setLoading(false);
    }
  }, [kind, status]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id, action) => {
    try {
      if (action === "delete") await api.delete(`/admin/discoveries/${id}`);
      else await api.post(`/admin/discoveries/${id}/${action}`);
      toast.success(action === "approve" ? "Published" : action === "reject" ? "Rejected" : "Deleted");
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const runNow = async (job) => {
    setRunning(true);
    try {
      await api.post(`/admin/discoveries/run/${job}`);
      toast.success("Research started — refresh in a minute");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div data-testid="admin-discoveries">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl uppercase tracking-tight text-white">AI discoveries</h1>
          <p className="mt-2 text-sm text-zinc-500">
            Daily research drafts. Nothing appears on the site until you approve it.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {JOBS.map((j) => (
            <button
              key={j.key}
              onClick={() => runNow(j.key)}
              disabled={running}
              data-testid={`discoveries-run-${j.key}`}
              className="inline-flex items-center gap-2 border border-white/20 px-4 py-2.5 text-[0.6rem] font-bold uppercase tracking-[0.16em] text-white hover:border-ochre hover:text-ochre disabled:opacity-50"
            >
              {running ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} {j.label}
            </button>
          ))}
          <button
            onClick={load}
            data-testid="discoveries-refresh"
            className="border border-white/20 px-4 py-2.5 text-[0.62rem] font-bold uppercase tracking-[0.16em] text-zinc-400 hover:text-white"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-6 border-b border-white/10 pb-4">
        <div className="flex gap-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setKind(t.key)}
              data-testid={`discoveries-tab-${t.key}`}
              className={`px-4 py-2 text-[0.62rem] font-bold uppercase tracking-[0.16em] transition-colors ${
                kind === t.key ? "bg-maasai text-white" : "text-zinc-500 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              data-testid={`discoveries-status-${s}`}
              className={`px-3 py-2 text-[0.6rem] uppercase tracking-[0.16em] transition-colors ${
                status === s ? "text-ochre underline underline-offset-4" : "text-zinc-600 hover:text-white"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="mt-10 text-sm text-zinc-500">Loading…</p>
      ) : failed ? (
        <div className="mt-10 border border-maasai/60 bg-[#140406] p-6" data-testid="discoveries-error">
          <p className="text-sm text-white">We could not load the review queue.</p>
          <button
            onClick={load}
            data-testid="discoveries-try-again"
            className="mt-4 border border-white/20 px-4 py-2.5 text-[0.6rem] font-bold uppercase tracking-[0.16em] text-zinc-300 hover:text-white"
          >
            Try again
          </button>
        </div>
      ) : items.length === 0 ? (
        <p className="mt-10 text-sm text-zinc-500" data-testid="discoveries-empty">
          Nothing in this queue. The daily job runs each morning, or use “Run research now”.
        </p>
      ) : (
        <div className="mt-8 space-y-4">
          {items.map((d) => (
            <div key={d.id} className="border border-white/10 bg-[#0b0b0b] p-5" data-testid={`discovery-${d.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-display text-lg uppercase tracking-tight text-white">{d.title}</p>
                  <p className="mt-1 text-[0.6rem] uppercase tracking-[0.16em] text-ochre">
                    {d.evidence || d.kind} · {d.status}
                  </p>
                  <p className="mt-3 max-w-3xl text-sm text-zinc-400">{d.summary}</p>
                  {d.kind === "player" ? (
                    <p className="mono mt-3 text-xs text-zinc-500">
                      {d.payload?.position} · {d.payload?.club} · {d.payload?.league} · {d.payload?.country}
                    </p>
                  ) : (
                    <p className="mono mt-3 text-xs text-zinc-500">
                      {d.payload?.game} · {d.payload?.category} · {d.payload?.date}
                    </p>
                  )}
                  <div className="mt-3 flex flex-wrap gap-4">
                    {(d.sources || []).map((s) => (
                      <a
                        key={s}
                        href={s}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[0.6rem] uppercase tracking-[0.14em] text-zinc-500 hover:text-ochre"
                      >
                        <ExternalLink size={11} /> source
                      </a>
                    ))}
                  </div>
                </div>
                <div className="flex shrink-0 gap-2">
                  {d.status === "pending" ? (
                    <>
                      <button
                        onClick={() => act(d.id, "approve")}
                        data-testid={`discovery-approve-${d.id}`}
                        className="inline-flex items-center gap-2 bg-maasai px-4 py-2.5 text-[0.6rem] font-bold uppercase tracking-[0.16em] text-white hover:bg-white hover:text-black"
                      >
                        <Check size={13} /> Approve
                      </button>
                      <button
                        onClick={() => act(d.id, "reject")}
                        data-testid={`discovery-reject-${d.id}`}
                        className="inline-flex items-center gap-2 border border-white/20 px-4 py-2.5 text-[0.6rem] font-bold uppercase tracking-[0.16em] text-zinc-400 hover:text-white"
                      >
                        <X size={13} /> Reject
                      </button>
                    </>
                  ) : null}
                  <button
                    onClick={() => act(d.id, "delete")}
                    data-testid={`discovery-delete-${d.id}`}
                    className="border border-white/15 p-2.5 text-zinc-500 hover:text-maasai"
                    aria-label="Delete discovery"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
